
import { useState, useEffect } from 'react';
import {
  analyzeCode,
  modifyCode,
  executeCode,
  searchCode,
  createCheckpoint,
  rollbackToCheckpoint,
  configureSparcAgent,
  initializeSparcMCP,
  getAvailableApiProviders,
  SparcConfig,
  CodeAnalysisResult,
  CodeModificationResult,
  CodeExecutionResult,
  CodeSearchResult,
  SparcLLMProvider
} from '@/services/agentic/sparcService';

interface UseSparcAgentProps {
  defaultConfig?: SparcConfig;
  autoInitialize?: boolean;
}

export const useSparcAgent = (props?: UseSparcAgentProps) => {
  const { defaultConfig, autoInitialize = true } = props || {};
  
  const [loading, setLoading] = useState<boolean>(false);
  const [analyzing, setAnalyzing] = useState<boolean>(false);
  const [modifying, setModifying] = useState<boolean>(false);
  const [executing, setExecuting] = useState<boolean>(false);
  const [searching, setSearching] = useState<boolean>(false);
  const [initialized, setInitialized] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [availableProviders, setAvailableProviders] = useState<SparcLLMProvider[]>([]);
  const [config, setConfig] = useState<SparcConfig>(defaultConfig || {
    model: 'gpt-4o-mini',
    executionMode: 'semi',
    diffMode: 'file',
    processingMode: 'sequential',
    apiProvider: 'openai'
  });
  
  // Analysis state
  const [analysisResult, setAnalysisResult] = useState<CodeAnalysisResult | null>(null);
  
  // Modification state
  const [modificationResult, setModificationResult] = useState<CodeModificationResult | null>(null);
  
  // Execution state
  const [executionResult, setExecutionResult] = useState<CodeExecutionResult | null>(null);
  
  // Search state
  const [searchResult, setSearchResult] = useState<CodeSearchResult | null>(null);
  
  // Initialize the agent on mount if autoInitialize is true
  useEffect(() => {
    if (autoInitialize) {
      initAgent();
    }
  }, [autoInitialize]);
  
  // Initialize the agent and get available API providers
  const initAgent = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const initResult = await initializeSparcMCP(config);
      
      if (initResult.success) {
        setInitialized(true);
        
        // If providers are available, update the config
        if (initResult.availableProviders && initResult.availableProviders.length > 0) {
          const defaultProvider = initResult.defaultProvider || initResult.availableProviders[0];
          
          setConfig(prevConfig => ({
            ...prevConfig,
            apiProvider: defaultProvider as 'openai' | 'anthropic'
          }));
          
          // Get detailed provider info
          const providers = await getAvailableApiProviders();
          setAvailableProviders(providers);
        }
      } else {
        setError(new Error("SPARC initialization failed"));
      }
      
      return initResult.success;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error during initialization');
      setError(error);
      setInitialized(false);
      return false;
    } finally {
      setLoading(false);
    }
  };
  
  // Update configuration
  const updateConfig = (newConfig: Partial<SparcConfig>) => {
    setConfig(prevConfig => ({
      ...prevConfig,
      ...newConfig
    }));
  };
  
  // Analyze code
  const analyze = async (files: string[], task?: string) => {
    setAnalyzing(true);
    setLoading(true);
    setError(null);
    
    try {
      const result = await analyzeCode(files, task, config);
      setAnalysisResult(result);
      return result;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error during analysis');
      setError(error);
      throw error;
    } finally {
      setAnalyzing(false);
      setLoading(false);
    }
  };
  
  // Modify code
  const modify = async (files: string[], task: string) => {
    setModifying(true);
    setLoading(true);
    setError(null);
    
    try {
      const result = await modifyCode(files, task, config);
      setModificationResult(result);
      return result;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error during modification');
      setError(error);
      throw error;
    } finally {
      setModifying(false);
      setLoading(false);
    }
  };
  
  // Execute code
  const execute = async (code: string, language: string) => {
    setExecuting(true);
    setLoading(true);
    setError(null);
    
    try {
      const result = await executeCode(code, language, config);
      setExecutionResult(result);
      return result;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error during execution');
      setError(error);
      throw error;
    } finally {
      setExecuting(false);
      setLoading(false);
    }
  };
  
  // Search code
  const search = async (query: string, limit?: number) => {
    setSearching(true);
    setLoading(true);
    setError(null);
    
    try {
      const result = await searchCode(query, limit, config);
      setSearchResult(result);
      return result;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error during search');
      setError(error);
      throw error;
    } finally {
      setSearching(false);
      setLoading(false);
    }
  };
  
  // Create checkpoint
  const checkpoint = async (name: string) => {
    setLoading(true);
    setError(null);
    
    try {
      return await createCheckpoint(name);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error creating checkpoint');
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };
  
  // Rollback to checkpoint
  const rollback = async (commit: string) => {
    setLoading(true);
    setError(null);
    
    try {
      return await rollbackToCheckpoint(commit);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error during rollback');
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };
  
  // Configure agent
  const configure = async (action: 'get' | 'set' | 'list', key?: string, value?: any) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await configureSparcAgent(action, key, value);
      // If setting a config value, update local config too
      if (action === 'set' && key) {
        setConfig(prevConfig => ({
          ...prevConfig,
          [key]: value
        }));
      }
      return result;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error during configuration');
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };
  
  return {
    // State
    loading,
    analyzing,
    modifying,
    executing,
    searching,
    initialized,
    error,
    config,
    availableProviders,
    
    // Results
    analysisResult,
    modificationResult,
    executionResult,
    searchResult,
    
    // Actions
    initAgent,
    updateConfig,
    analyze,
    modify,
    execute,
    search,
    checkpoint,
    rollback,
    configure
  };
};
